import { BAOCustomElement } from '@bao/custom-element'
import { Cart } from '@bao/cart'
import { eventBus, EVENTS } from '@bao/events'
import { sectionRenderer } from '@bao/section-rendering'
import { debounce } from '@bao/utils'

class CartItem extends BAOCustomElement() {
  static get requiredElements () {
    return ['remove']
  }

  constructor () {
    super()

    this._loading = false
    this.sections = this.inPushCart
      ? sectionRenderer.pushCartSections
      : this.onCartPage
        ? sectionRenderer.cartSections
        : []
  }

  setupListeners () {
    super.setupListeners()

    this.removeLineItem = this.removeLineItem.bind(this)
    this.onQuantityChange = this.onQuantityChange.bind(this)
    this.debouncedOnQuantityChange = debounce((event) => {
      this.onQuantityChange(event)
    }, 500)

    if (this.els?.remove.exists) {
      this.listeners.add(this.els.remove.element, 'click', this.removeLineItem)
    }

    this.listeners.add(
      this,
      EVENTS.QUANTITY_ADJUSTER.CHANGE,
      this.debouncedOnQuantityChange,
    )

    this.onItemPropertiesUpdate = this.onItemPropertiesUpdate.bind(this)

    eventBus.addEventListener(
      EVENTS.PUSH_CART.AFTER_ITEM_PROPERTIES_UPDATE,
      this.onItemPropertiesUpdate,
    )
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    eventBus.removeEventListener(
      EVENTS.PUSH_CART.AFTER_ITEM_PROPERTIES_UPDATE,
      this.onItemPropertiesUpdate,
    )
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  async removeLineItem (e) {
    e.preventDefault()

    this.loading = true

    const attributes = {
      removed_line_item: this.lineItem,
    }

    await Cart.updateCartAttributes(attributes, {
      sections: [],
    })

    const state = await Cart.removeItem(this.dataset.key, {
      sections: this.mappedSections,
    })

    this.loading = false

    if ('sections' in state) this.renderSections(state.sections)
  }

  async onQuantityChange (e) {
    this.loading = true

    const state = await Cart.updateQuantity(this.dataset.key, e.detail.value, {
      sections: this.mappedSections,
    })

    this.loading = false

    if (!state) {
      this.handleCartErrorRender()

      return
    }

    if ('sections' in state) this.renderSections(state.sections)
  }

  onItemPropertiesUpdate (e) {
    const { state } = e.detail

    if (!state) {
      this.handleCartErrorRender()
      return
    }

    const sections = Object.values(state.sections)
    if (!sections.length) return

    const newDOM = sectionRenderer.convertToDOM(sections[0])
    const myself = newDOM.querySelector(
      `cart-item[data-index="${this.lineItem.index}"][data-product-id="${this.lineItem.product_id}"]`,
    )

    if (!myself) return

    this.outerHTML = myself.outerHTML
  }

  get mappedSections () {
    return this.sections.map((s) => s.section)
  }

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    if (this.inPushCart) {
      this.pushCart.loading = val
    }

    if (this.onCartPage) {
      this.cart.loading = val
    }

    this.setAttribute('aria-busy', val.toString())
  }

  get pushCart () {
    return this.closest('push-cart')
  }

  get inPushCart () {
    return this.pushCart != null
  }

  get cart () {
    return this.closest('cart-page')
  }

  get onCartPage () {
    return this.cart != null
  }

  get lineItem () {
    return {
      index: this.dataset.index || 0,
      product_id: this.dataset.productId || 0,
      product_image: this.dataset.productImage || '',
      product_size: this.dataset.productSize || '',
      product_title: this.dataset.productTitle || '',
      product_url: this.dataset.productUrl || '',
      variant_id: this.dataset.variantId || 0,
      variant_url: this.dataset.variantUrl || '',
    }
  }
}

if (!customElements.get('cart-item')) {
  customElements.define('cart-item', CartItem)
}
